import { type JSX } from "react";
import { useSceneSummaries } from "../../api/hooks/useSceneSummaries";
import { formatIsoToLocale } from "../../utils/formatIsoDate";

/**
 * scene-summary-feed (Phase γ, Task 7.1) reader UI component。
 *
 * `GET /scenarios/:scenarioId/scene-summaries?page=N&limit=20` を呼び出し、
 * 匿名 (= reader_id / playlineId 含めず) の scene summary feed を chronological
 * order (古い順 → 新しい順、 server 側で generatedAt asc) で表示する。
 *
 * design.md §Component 9 / Req 7 AC 4-6 対応:
 *  - scenarioId prop 受取
 *  - 初回 fetch (page=1, limit=20)
 *  - 「次へ」 button で次 page 取得 (= summaries 累積)
 *  - summary 0 件 → 「まだ scene 要約がありません」 placeholder
 *
 * fetch は openapi-typescript の schema.d.ts に該当 path 未登録 (= Task 6.1 で
 * OAS 外手動登録経路を採用) のため、useSceneSummaries hook 内で raw fetch する。
 * 既存 design system component / className を再利用 (= 新規 design token 追加なし)。
 *
 * 既存 character-codex / palimpsest 等の features 配下とは異なり、 spec で
 * `src/components/scenario/` 配下を mandate されているため本 file をそこに置く。
 */

/**
 * BASE_URL は call 時に都度 resolve する (= test で `import.meta.env` を上書き
 * してから component import するパターンを許容するため、 module 読込時 const
 * 評価にはしない)。 production では env が固定なので結果同じ。
 */
function resolveBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1"
  );
}

const PAGE_SIZE = 20;

export type { SceneSummaryItem } from "../../api/hooks/useSceneSummaries";

export interface SceneSummaryFeedProps {
  scenarioId: string;
}

export function SceneSummaryFeed({
  scenarioId,
}: SceneSummaryFeedProps): JSX.Element {
  const state = useSceneSummaries(resolveBaseUrl(), scenarioId, PAGE_SIZE);
  const handleLoadMore = (): void => state.loadNextPage();
  const handleRetry = (): void => state.retry();
  const isInitialLoad = state.isLoading;
  const isEmpty =
    !state.isLoading &&
    state.error === null &&
    state.summaries.length === 0;

  return (
    <div
      data-testid="scene-summary-feed"
      className="flex flex-col gap-3 text-reader-primary"
    >
      <h3 className="text-base font-semibold text-reader-primary">
        他の世界線の足跡
      </h3>

      {isInitialLoad && (
        <div
          data-testid="scene-summary-feed-loading"
          className="text-sm text-reader-muted"
        >
          読み込み中…
        </div>
      )}

      {state.error !== null && (
        <div
          data-testid="scene-summary-feed-error"
          role="alert"
          className="flex flex-col gap-2 text-sm text-rose-300"
        >
          <span>
            summary feed の取得に失敗しました ({state.error.message})
          </span>
          <button
            type="button"
            onClick={handleRetry}
            className="self-start px-3 py-1 text-xs bg-rose-900/40 hover:bg-rose-800/60 text-rose-100 rounded border border-rose-700/50"
          >
            再試行
          </button>
        </div>
      )}

      {isEmpty && (
        <div
          data-testid="scene-summary-feed-empty"
          className="text-sm text-reader-muted"
        >
          まだ scene 要約がありません
        </div>
      )}

      {state.summaries.length > 0 && (
        <ul
          data-testid="scene-summary-feed-list"
          className="flex flex-col gap-3"
        >
          {state.summaries.map((summary) => (
            <li
              key={summary.id}
              data-testid={`scene-summary-feed-item-${summary.id}`}
              className="flex flex-col gap-1 p-3 bg-reader-surface rounded border border-reader-border"
            >
              <p className="text-sm text-reader-primary whitespace-pre-wrap">
                {summary.summaryText}
              </p>
              <time
                dateTime={summary.generatedAt}
                className="text-xs text-reader-muted"
              >
                {/* 日付表示は読者が「いつの summary か」を粗く把握できれば十分なため、
                    locale-default の short 表記で format する (= 詳細粒度は future spec)。
                    server が ISO 文字列で返す前提だが、 防御的に raw を出して観測性を確保。 */}
                {formatIsoToLocale(summary.generatedAt) ?? summary.generatedAt}
              </time>
            </li>
          ))}
        </ul>
      )}

      {state.hasNextPage && state.error === null && (
        <button
          type="button"
          data-testid="scene-summary-feed-load-more"
          onClick={handleLoadMore}
          disabled={state.isLoadingNextPage}
          className="self-start px-3 py-1 text-sm bg-reader-surface hover:bg-reader-border text-reader-secondary rounded border border-reader-border-strong disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {state.isLoadingNextPage ? "読み込み中…" : "次へ"}
        </button>
      )}
    </div>
  );
}
